/* eslint-disable max-statements */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Domain, Status } from '@tiendanube/common/src/enums';
import { FEATURE_SEARCH_FILTER } from 'App/features';
import {
  ORDERS_ALLOW_PRODUCT_NAME_FILTER,
  PAYMENT_STATUS_EXPIRED_ENABLED,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useResponsive } from 'commons/components';
import { OptionType } from 'commons/components/MultiSelect/multiSelectStyleConfig';
import { useBoolean } from 'commons/hooks';
import { SearchFilterInterfaceIdLabel } from 'commons/types';
import { dateFormat, Format } from 'commons/utils/date';
import { ORDERS_SEARCH_ORDERS } from 'config/upsellFlowSources';
import { useGetTags } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { UpsellResultEnum } from 'domains/Billing/UpsellFlow/hooks/useUpsellFlow';
import formatDateFilterChips from 'domains/Metafields/utils/formatDateFilterChips';
import { ProductUnitsCountFilterValue } from 'domains/Orders/components/ProductUnitsCountFilter';
import { useProductUnitsCountFilter } from 'domains/Orders/components/ProductUnitsCountFilter/useProductUnitsCountFilter';
import {
  CreateSavedSearchModal,
  SavedSearchesConfigModal,
  useSelectedSavedSearch,
} from 'domains/Orders/components/SavedSearches';
import { METAFIELD_FILTER_PREFIX } from 'domains/Orders/constants';
import { useOrdersList } from 'domains/Orders/Orders/hooks';
import useGetOrdersFilters from 'domains/Orders/Orders/hooks/useGetOrdersFilters/useGetOrdersFilters';
import { useSavedSearchesUpsell } from 'domains/Orders/Orders/hooks/useSavedSearchesUpsell';
import {
  defaultFilters,
  FiltersParamsType,
  FilterStatusEnum,
  fulfillmentStatusFilter,
  locationFilter,
  paymentStatusFilter,
  ProductCountOptionsEnum,
  shippingMethodFilter,
  statusFilter,
  PIPE_SEPARATOR,
  COMMA_SEPARATOR,
  paymentMethodsFilter,
  paymentProviderFilter,
  originFilter,
} from 'domains/Orders/Orders/ordersService';
import { getOrdersAggregations } from 'domains/Orders/Orders/ordersSlice';
import { useOrdersMetafields } from 'domains/Orders/Orders/pages/hooks';
import { SearchFilter } from 'domains/Orders/Orders/pages/OrderListPage/components/OrderListFilter/SearchFilter';
import {
  getLocationChips,
  getPaymentMethodChips,
  getPaymentProviderChips,
  getShippingMethodChips,
  getStatusChips,
} from 'domains/Orders/Orders/utils/OrderListFilter';
import {
  trackingOrderSearch,
  trackingOrderListFilterClick,
  trackingFilterChipClose,
  trackingClearFilters,
  trackingOrdersSearchFilterClick,
  trackingSavedSearchQuickSave,
} from 'domains/Orders/tracking';
import { useGetShippingMethods } from 'domains/Shipping/DeliveryMethods/hooks';
import useLocations from 'domains/Shipping/Locations/hooks/useLocations/useLocations';
import FilterModal from './FilterModal';
import { SearchFilterMobile } from './SearchFilterMobile';
import { useFilterSelectedProducts } from '../../hooks/useFilterSelectedProducts';

interface OrderListFilterProps {
  showModal: boolean;
  filters: FiltersParamsType;
  ordersCount: number;
  isLoading: boolean;
  onChange: (filters: FiltersParamsType) => void;
  onOpenModal: () => void;
  onCloseModal: () => void;
  handleChangeSavedSearch: (newFilters: FiltersParamsType) => void;
}

function OrderListFilter({
  showModal,
  filters,
  ordersCount,
  isLoading,
  onChange,
  onOpenModal,
  onCloseModal,
  handleChangeSavedSearch,
}: OrderListFilterProps): JSX.Element | null {
  const { t } = useTranslation([Domain.ORDERS]);
  const { shippingMethods, fetchShippingMethods } = useGetShippingMethods();
  const { locations, fetchLocations } = useLocations();
  const {
    paymentProvidersOrdersFilters,
    applicationOrdersFilters,
    fetchOrdersFilters,
  } = useGetOrdersFilters();
  const { statusSuccess } = useOrdersList();
  const { isDesktop } = useResponsive();
  const tags = useGetTags();
  const [hasExpiredTag] = useHasTags([PAYMENT_STATUS_EXPIRED_ENABLED]);
  const { fetchMetafields } = useOrdersMetafields();
  const { selectedSavedSearch } = useSelectedSavedSearch(filters);

  const aggregations = useSelector(getOrdersAggregations);
  const [query, setQuery] = useState(filters.q || '');
  const handleChangeQuery = (value: string) => {
    setQuery(value);
  };

  const [isSavedSearchModalOpen, openSavedSearchModal, closeSavedSearchModal] =
    useBoolean(false);
  const [
    isSavedSearchesConfigModalOpen,
    showSavedSearchesConfigModal,
    hideSavedSearchesConfigModal,
  ] = useBoolean(false);

  const {
    selectedProducts,
    setSelectedProducts,
    serializeProducts,
    deserializeProducts,
  } = useFilterSelectedProducts(filters.products, aggregations);
  const { getProductsCountFilterOption } = useProductUnitsCountFilter();

  const [productUnitsCountFilterValue, setProductUnitsCountFilterValue] =
    useState<ProductUnitsCountFilterValue>({
      selectedOption: getProductsCountFilterOption(
        filters.minUnits,
        filters.maxUnits,
      ),
      minUnits: filters.minUnits,
      maxUnits: filters.maxUnits,
    });
  const [couponOptions, setCouponOptions] = useState<OptionType[]>([]);

  const handleOpenModal = () => {
    onOpenModal();
    trackingOrderListFilterClick();
  };

  const handleConfirmSavedSearch = (ssFilters: FiltersParamsType) => {
    onChange(ssFilters);
    hideSavedSearchesConfigModal();
  };

  const handleChangeText = useUpsellFlow({
    title: t('filter.upsell.search'),
    featureKey: FEATURE_SEARCH_FILTER,
    trackingSource: ORDERS_SEARCH_ORDERS,
    includeCallbackResult: true,
    callback: (upsellResult: UpsellResultEnum, text: string) => {
      onChange({ ...filters, status: FilterStatusEnum.ALL, q: text });
      trackingOrderSearch(text);
      trackingOrdersSearchFilterClick(upsellResult, 'search');
    },
    onCloseModal: (upsellResult) => {
      if (upsellResult === UpsellResultEnum.DECLINED) {
        trackingOrdersSearchFilterClick(upsellResult, 'search');
      }
    },
  });

  const handleClearFilterButtonClick = () => {
    setProductUnitsCountFilterValue({
      selectedOption: ProductCountOptionsEnum.ALL,
      minUnits: '',
      maxUnits: '',
    });
    setSelectedProducts([]);
    trackingClearFilters();
    onChange(defaultFilters);
  };

  const handleSaveFilterButtonClick = useSavedSearchesUpsell(() => {
    trackingSavedSearchQuickSave('applied_filters');
    openSavedSearchModal();
  });

  const handleVariantDismiss = (key: string): boolean => {
    if (!key.startsWith('variant-')) return false;

    const variantId = key.substring(key.indexOf('-') + 1);
    const newValue = serializeProducts(
      selectedProducts.filter((p) => p.variantId !== variantId),
    );
    setSelectedProducts((prev) =>
      prev.filter((p) => p.variantId !== variantId),
    );

    trackingFilterChipClose();
    onChange({
      ...filters,
      products: newValue,
      exactProductSet: newValue === '' ? '' : filters.exactProductSet,
    });
    return true;
  };

  const handleUnitsDismiss = (key: string): boolean => {
    if (key !== 'units') return false;

    const unitsValue = { minUnits: '', maxUnits: '' };
    setProductUnitsCountFilterValue({
      selectedOption: ProductCountOptionsEnum.ALL,
      ...unitsValue,
    });
    trackingFilterChipClose();
    onChange({
      ...filters,
      ...unitsValue,
    });
    return true;
  };

  const handleCouponDismiss = (key: string): boolean => {
    if (!key.startsWith('couponIdsRaw-')) return false;
    if (!filters.couponIdsRaw) return true;

    const couponIdToRemove = key.substring(key.indexOf('-') + 1);
    const remainingCouponIds = filters.couponIdsRaw
      .split(',')
      .filter((id) => id !== couponIdToRemove)
      .join(',');

    trackingFilterChipClose();
    onChange({ ...filters, couponIdsRaw: remainingCouponIds });
    return true;
  };

  const handleMultiValueFilterDismiss = (
    key: string,
  ): { handled: boolean; filterKey: string; newValue: string } => {
    const multiValueFilters = [
      shippingMethodFilter,
      locationFilter,
      paymentStatusFilter,
      fulfillmentStatusFilter,
      paymentMethodsFilter,
      paymentProviderFilter,
      statusFilter,
      originFilter,
    ];
    const matchedFilter = multiValueFilters.find((filter) =>
      key.startsWith(`${filter}-`),
    );

    if (!matchedFilter) {
      return { handled: false, filterKey: key, newValue: '' };
    }

    const firstHyphenIndex = key.indexOf('-');
    const filterKey = key.substring(0, firstHyphenIndex);
    const filterValue = key.substring(firstHyphenIndex + 1);
    const currentValue = filters[filterKey] || '';
    const separator =
      filterKey === paymentProviderFilter ? PIPE_SEPARATOR : COMMA_SEPARATOR;

    let newValue = currentValue
      .split(separator)
      .filter((val) => val.trim() !== filterValue)
      .join(separator);

    // If removing 'voided' from paymentStatus without tag, also remove 'expired'
    if (
      filterKey === paymentStatusFilter &&
      filterValue === Status.VOIDED &&
      !hasExpiredTag
    ) {
      newValue = newValue
        .split(separator)
        .filter((val) => val.trim() !== Status.EXPIRED)
        .join(separator);
    }

    return { handled: true, filterKey, newValue };
  };

  const handleDismiss = (key: string) => {
    if (handleVariantDismiss(key)) return;
    if (handleUnitsDismiss(key)) return;
    if (handleCouponDismiss(key)) return;

    const multiValueResult = handleMultiValueFilterDismiss(key);
    if (multiValueResult.handled) {
      trackingFilterChipClose();
      onChange({
        ...filters,
        [multiValueResult.filterKey]: multiValueResult.newValue,
      });
      return;
    }

    trackingFilterChipClose();
    onChange({ ...filters, [key]: '' });
  };
  const withoutResult = statusSuccess && ordersCount === 0;

  const appliedFilters: SearchFilterInterfaceIdLabel[] = useMemo(() => {
    const unitsFilter = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'minUnits') unitsFilter['units'] = value;
      if (key === 'maxUnits' && unitsFilter['units'])
        unitsFilter['units'] = unitsFilter['units'].concat(`,${value}`);
    });

    return Object.entries({ ...filters, ...unitsFilter })
      .filter(
        ([key, value]) =>
          value !== '' &&
          key !== 'page' &&
          key !== 'perPage' &&
          key !== 'exactProductSet' &&
          key !== 'minUnits' &&
          key !== 'maxUnits' &&
          !(
            key === statusFilter &&
            (value === FilterStatusEnum.EMPTY || value === Status.OPEN)
          ),
      )
      .flatMap(
        ([key, value]):
          | SearchFilterInterfaceIdLabel
          | SearchFilterInterfaceIdLabel[]
          | undefined => {
          if (key === shippingMethodFilter) {
            return getShippingMethodChips(
              value as string,
              key,
              shippingMethods,
            );
          }
          if (key === locationFilter) {
            return getLocationChips(value as string, key, locations);
          }
          if (key === paymentProviderFilter && paymentProvidersOrdersFilters) {
            return getPaymentProviderChips(
              value as string,
              key,
              t,
              paymentProvidersOrdersFilters,
            );
          }
          if (key === 'dateFrom') {
            const label = t('appliedFilters.dateFrom', {
              dateFrom: dateFormat(
                value as string,
                Format.DAY_MONTH_YEAR_NUMBER,
              ),
            });
            return { id: key, label: label };
          }
          if (key === 'dateTo') {
            const label = t('appliedFilters.dateTo', {
              dateTo: dateFormat(value as string, Format.DAY_MONTH_YEAR_NUMBER),
            });
            return { id: key, label: label };
          }
          if (key === statusFilter) {
            return getStatusChips(value as string, key, t);
          }
          if (key === paymentMethodsFilter) {
            return getPaymentMethodChips(value as string, key, t);
          }
          if (key === 'appId' && applicationOrdersFilters) {
            const label = Object.values(applicationOrdersFilters)
              .flat()
              .find(({ id }) => id === value)?.name;
            return { id: key, label: label ?? '' };
          }
          if (key === originFilter) {
            return value
              .toString()
              .split(',')
              .map((origin) => ({
                id: `${key}-${origin.trim()}`,
                label: t(`filters.origin.${origin.trim()}`),
              }));
          }
          if (key === 'products') {
            const deserializedProducts = deserializeProducts(value.toString());
            return aggregations?.['variants']
              ?.map((product) => {
                if (
                  deserializedProducts.find(
                    (dp) => dp.variantId === product.key.toString(),
                  )
                ) {
                  return product;
                }
                return undefined;
              })
              .filter((p) => !!p && !!p.label)
              .map((product): SearchFilterInterfaceIdLabel => {
                // Product should always be set but typescript check doesn't realize it
                if (!product) {
                  return { id: '', label: '' };
                }
                return {
                  id: `variant-${product.key}`,
                  label: product.label,
                  badgeCount: product.total,
                };
              });
          }
          if (key === 'units') {
            const values = value.toString().split(',');
            let label;
            if (values[0] === values[1])
              label = t(`appliedFilters.productsCount.units`, {
                units: values[0],
              });
            else if (values[1] !== '')
              label = t(`appliedFilters.productsCount.unitsRange`, {
                minUnits: values[0],
                maxUnits: values[1],
              });
            else
              label = t(`appliedFilters.productsCount.unitsRangeWithoutLimit`, {
                minUnits: values[0],
              });
            return {
              id: key,
              label,
            };
          }
          if (key === paymentStatusFilter || key === fulfillmentStatusFilter) {
            let filterValue = value.toString();

            // Filter out 'expired' payment status if user doesn't have the tag
            if (key === paymentStatusFilter && !hasExpiredTag) {
              filterValue = filterValue
                .split(',')
                .filter((s) => s.trim() !== Status.EXPIRED)
                .join(',');
            }

            // Return empty array if filter value is empty after filtering
            if (!filterValue.trim()) {
              return [];
            }

            return filterValue.split(',').map((val) => ({
              id: `${key}-${val}`,
              label: t(`appliedFilters.${val}`, val as string),
            }));
          }

          if (key === 'isWholesale') {
            const label = t(`appliedFilters.b2bFilter.${value}`);
            return { id: key, label };
          }
          if (key === 'isSubscription') {
            const label = t(`appliedFilters.isSubscriptionFilter.${value}`);
            return { id: key, label };
          }
          if (key === 'couponIdsRaw') {
            if (couponOptions.length === 0) return [];

            const buildCouponLabel = (val: string) => {
              const opt = couponOptions.find((o) => o.value.toString() === val);
              if (!opt) return null;

              return {
                id: `${key}-${val}`,
                label: opt.label,
              };
            };

            return value
              .toString()
              .split(',')
              .map(buildCouponLabel)
              .filter((f): f is { id: string; label: string } => f !== null);
          }

          if (key === 'stockIssues' && value === 'true') {
            return {
              id: key,
              label: t('appliedFilters.stockIssues'),
            };
          }

          return {
            id: key,
            label: t(`appliedFilters.${value}`, value as string),
          };
        },
      )
      .filter((f) => !!f)
      .map((filter) => {
        // Will never happen because of the previous filter but typescript check bugs with this case
        if (!filter) {
          return { id: '', label: '' };
        }

        const isMetafieldFilter = filter.id.startsWith(METAFIELD_FILTER_PREFIX);

        return {
          ...filter,
          label: isMetafieldFilter
            ? formatDateFilterChips(filter.label)
            : filter.label,
          removable: true,
        };
      });
  }, [
    aggregations,
    deserializeProducts,
    applicationOrdersFilters,
    filters,
    locations,
    paymentProvidersOrdersFilters,
    shippingMethods,
    t,
    couponOptions,
    hasExpiredTag,
  ]);

  const desktopPlaceholder = tags.includes(ORDERS_ALLOW_PRODUCT_NAME_FILTER)
    ? t('filter.inputLabelDesktopProductName')
    : t('filter.inputLabelDesktop');

  const placeholder = isDesktop ? desktopPlaceholder : t('filter.inputLabel');

  function buildResultCount(): string {
    return `${t('filter.result', { count: ordersCount })} ${t(
      'filter.filteringBy',
    )}`;
  }

  const resultCount = isLoading ? '' : buildResultCount();
  const resultCountLabel = withoutResult ? '' : resultCount;
  const label = isDesktop ? t('filter.inputAriaLabel') : '';

  useEffect(() => {
    fetchMetafields();
  }, [fetchMetafields]);

  useEffect(() => {
    fetchShippingMethods();
  }, [fetchShippingMethods]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    fetchOrdersFilters();
  }, [fetchOrdersFilters]);

  useEffect(() => {
    setQuery(filters.q ?? '');
  }, [filters.q]);

  return (
    <>
      {isDesktop ? (
        <SearchFilter
          resultCount={resultCountLabel}
          isLoading={isLoading}
          filters={filters}
          appliedFilters={appliedFilters}
          value={query}
          handleChangeSavedSearch={handleChangeSavedSearch}
          onClearFilterButtonClick={handleClearFilterButtonClick}
          onSaveFilterButtonClick={handleSaveFilterButtonClick}
          onSavedSearchesConfigClick={showSavedSearchesConfigModal}
          onSubmit={handleChangeText}
          onClick={handleOpenModal}
          onDismiss={handleDismiss}
          onChange={handleChangeQuery}
          showSaveFiltersButton={!selectedSavedSearch}
        />
      ) : (
        <SearchFilterMobile
          placeholder={placeholder}
          onSubmit={handleChangeText}
          onClick={handleOpenModal}
          onDismiss={handleDismiss}
          resultCount={resultCountLabel}
          handleChangeSavedSearch={handleChangeSavedSearch}
          isLoading={isLoading}
          appliedFilters={appliedFilters}
          filters={filters}
          ariaLabel={t('filter.inputAriaLabel')}
          label={label}
          onChange={handleChangeQuery}
          onClearFilterButtonClick={handleClearFilterButtonClick}
          onSaveFilterButtonClick={handleSaveFilterButtonClick}
          onSavedSearchesConfigClick={showSavedSearchesConfigModal}
          showSaveFiltersButton={!selectedSavedSearch}
          value={query}
        />
      )}
      <FilterModal
        show={showModal}
        hideModal={onCloseModal}
        filter={filters}
        addParam={onChange}
        selectedProducts={selectedProducts}
        addSelectedProductParam={setSelectedProducts}
        serializeProducts={serializeProducts}
        productUnitsCountFilterValue={productUnitsCountFilterValue}
        addProductUnitsCountFilterParam={setProductUnitsCountFilterValue}
        onCouponOptionsLabel={setCouponOptions}
      />
      <SavedSearchesConfigModal
        open={isSavedSearchesConfigModalOpen}
        onDismiss={hideSavedSearchesConfigModal}
        onCouponOptionsLabel={setCouponOptions}
        onConfirmSavedSearch={handleConfirmSavedSearch}
      />
      <CreateSavedSearchModal
        open={isSavedSearchModalOpen}
        onDismiss={closeSavedSearchModal}
        onConfirm={closeSavedSearchModal}
        filters={filters}
      />
    </>
  );
}

export default OrderListFilter;
